import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Ticket, Plus, Copy, XCircle, Sparkles } from "lucide-react";

// Admin gate: trust backend `is_admin` (source of truth = ADMIN_EMAIL env).

function formatExpires(ts) {
  if (!ts) return "No expiry";
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

function StatusBadge({ p }) {
  const now = Math.floor(Date.now() / 1000);
  const expired = p.expires_at && p.expires_at < now;
  const exhausted = p.max_redemptions && p.times_redeemed >= p.max_redemptions;
  const label = !p.active ? "Deactivated" : expired ? "Expired" : exhausted ? "Used up" : "Active";
  const color = label === "Active" ? "var(--moss)" : "var(--ink-soft)";
  const bg = label === "Active" ? "rgba(111,125,85,0.12)" : "rgba(0,0,0,0.06)";
  return (
    <span
      data-testid={`promo-status-${p.code}`}
      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{ backgroundColor: bg, color }}
    >
      {label}
    </span>
  );
}

function CreateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",       // "percent" | "amount"
    percent_off: 20,
    amount_off: 5,
    duration: "once",               // "once" | "forever" | "repeating"
    duration_amount: 3,
    duration_unit: "months",        // days | weeks | months | years
    max_redemptions: "",
    expires_at: "",
    description: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error("Code is required.");
      return;
    }
    const body = {
      code: form.code.trim().toUpperCase(),
      duration: form.duration,
      description: form.description.trim() || undefined,
    };
    if (form.discount_type === "percent") {
      const pct = parseInt(form.percent_off, 10);
      if (!(pct >= 1 && pct <= 100)) {
        toast.error("Percent off must be between 1 and 100.");
        return;
      }
      body.percent_off = pct;
    } else {
      const amt = parseFloat(form.amount_off);
      if (!(amt >= 0.5)) {
        toast.error("Amount off must be at least $0.50.");
        return;
      }
      body.amount_off = amt;
    }
    if (form.duration === "repeating") {
      const n = parseInt(form.duration_amount, 10);
      if (!(n >= 1)) {
        toast.error("Duration amount must be at least 1.");
        return;
      }
      body.duration_amount = n;
      body.duration_unit = form.duration_unit;
    }
    if (form.max_redemptions) body.max_redemptions = parseInt(form.max_redemptions, 10);
    if (form.expires_at) body.expires_at = form.expires_at;

    setBusy(true);
    try {
      const { data } = await api.post("/admin/promo-codes", body);
      const discountLbl = data.amount_off
        ? `$${data.amount_off.toFixed(2)} off`
        : `${data.percent_off}% off`;
      toast.success(`Created ${data.code} — ${discountLbl}`);
      onCreated?.(data);
      onOpenChange(false);
      setForm({
        code: "", discount_type: "percent", percent_off: 20, amount_off: 5,
        duration: "once", duration_amount: 3, duration_unit: "months",
        max_redemptions: "", expires_at: "", description: "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create");
    } finally { setBusy(false); }
  };

  const isPct = form.discount_type === "percent";
  const isRepeat = form.duration === "repeating";
  // Preview the effective coverage.
  const preview = (() => {
    const disc = isPct
      ? `${form.percent_off}% off`
      : `$${Number(form.amount_off || 0).toFixed(2)} off`;
    if (form.duration === "once") return `${disc} the first billing cycle`;
    if (form.duration === "forever") return `${disc} every month, forever`;
    return `${disc} for ${form.duration_amount} ${form.duration_unit}`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="promo-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl inline-flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: "var(--terracotta)" }} />
            New promo code
          </DialogTitle>
          <DialogDescription>
            Percent or fixed-dollar off Maker Pro / Organizer Pro. Duration
            can be once, forever, or a specific window (days / weeks / months
            / years). Codes are created directly in Stripe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="promo-create-form">
          <div>
            <Label className="text-xs uppercase tracking-widest">Code</Label>
            <Input
              data-testid="promo-code-input"
              className="mt-2 uppercase font-mono tracking-widest"
              value={form.code}
              onChange={upd("code")}
              placeholder="LAUNCH50"
              maxLength={32}
              required
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>
              Auto-uppercased. Letters + numbers only. What vendors will type.
            </p>
          </div>

          {/* Discount type */}
          <div>
            <Label className="text-xs uppercase tracking-widest">Discount type</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="promo-type-percent"
                onClick={() => setField("discount_type", "percent")}
                className="text-sm rounded-md border py-2 px-3 text-left"
                style={{
                  borderColor: isPct ? "var(--terracotta)" : "rgba(0,0,0,0.15)",
                  background: isPct ? "rgba(196,79,54,0.06)" : "#fff",
                }}
              >
                <div className="font-medium">Percent off</div>
                <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                  e.g. 20% off
                </div>
              </button>
              <button
                type="button"
                data-testid="promo-type-amount"
                onClick={() => setField("discount_type", "amount")}
                className="text-sm rounded-md border py-2 px-3 text-left"
                style={{
                  borderColor: !isPct ? "var(--terracotta)" : "rgba(0,0,0,0.15)",
                  background: !isPct ? "rgba(196,79,54,0.06)" : "#fff",
                }}
              >
                <div className="font-medium">Fixed dollars off</div>
                <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                  e.g. $5 off
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {isPct ? (
              <div>
                <Label className="text-xs uppercase tracking-widest">Percent off</Label>
                <Input
                  data-testid="promo-percent-input" type="number" min="1" max="100"
                  className="mt-2" value={form.percent_off} onChange={upd("percent_off")} required
                />
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-widest">Dollars off</Label>
                <Input
                  data-testid="promo-amount-input" type="number" min="0.5" step="0.5"
                  className="mt-2" value={form.amount_off} onChange={upd("amount_off")} required
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">Max redemptions</Label>
              <Input
                data-testid="promo-max-input" type="number" min="1"
                className="mt-2" value={form.max_redemptions} onChange={upd("max_redemptions")}
                placeholder="Unlimited"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <Label className="text-xs uppercase tracking-widest">How long does it apply?</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { v: "once", label: "First bill", hint: "Billing cycle 1 only" },
                { v: "repeating", label: "For a while", hint: "Days / weeks / months / years" },
                { v: "forever", label: "Forever", hint: "Every bill, always" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`promo-duration-${opt.v}`}
                  onClick={() => setField("duration", opt.v)}
                  className="text-sm rounded-md border py-2 px-3 text-left"
                  style={{
                    borderColor: form.duration === opt.v ? "var(--terracotta)" : "rgba(0,0,0,0.15)",
                    background: form.duration === opt.v ? "rgba(196,79,54,0.06)" : "#fff",
                  }}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>{opt.hint}</div>
                </button>
              ))}
            </div>
            {isRepeat && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">How many</Label>
                  <Input
                    data-testid="promo-duration-amount" type="number" min="1" max="120"
                    className="mt-2" value={form.duration_amount} onChange={upd("duration_amount")}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Unit</Label>
                  <select
                    data-testid="promo-duration-unit"
                    value={form.duration_unit}
                    onChange={upd("duration_unit")}
                    className="mt-2 w-full h-10 rounded-md border px-3 text-sm"
                    style={{ borderColor: "rgba(0,0,0,0.15)", background: "#fff" }}
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
              </div>
            )}
            {isRepeat && (form.duration_unit === "days" || form.duration_unit === "weeks") && (
              <p
                className="mt-2 text-[11px] rounded-md p-2"
                style={{ background: "rgba(212,171,79,0.14)", color: "#7a5b12" }}
                data-testid="promo-rounding-note"
              >
                Note: Stripe applies discounts by billing cycle, so days & weeks
                are rounded up to the nearest full month behind the scenes.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Expires on</Label>
            <Input
              data-testid="promo-expires-input" type="date"
              className="mt-2" value={form.expires_at} onChange={upd("expires_at")}
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>Leave blank for no expiration.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Internal note</Label>
            <Input
              data-testid="promo-desc-input" maxLength={140}
              className="mt-2" value={form.description} onChange={upd("description")}
              placeholder="Launch cohort · Twitter announcement"
            />
          </div>

          <div
            className="rounded-md p-3 text-sm"
            style={{ background: "rgba(111,125,85,0.08)", color: "var(--moss)" }}
            data-testid="promo-preview"
          >
            <strong>{form.code.trim().toUpperCase() || "NEWCODE"}</strong> · {preview}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full"
                    onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" className="btn-primary rounded-full"
                    data-testid="promo-create-submit" disabled={busy}>
              {busy ? "Creating…" : "Create code"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPromoCodes() {
  const { user } = useAuth();
  const [codes, setCodes] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);

  const load = () => {
    api.get("/admin/promo-codes")
      .then(({ data }) => setCodes(data.items || []))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        setCodes([]);
      });
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch { toast.error("Copy failed"); }
  };

  const deactivate = async (id, code) => {
    if (!window.confirm(`Deactivate ${code}? Vendors won’t be able to use it anymore.`)) return;
    try {
      await api.post(`/admin/promo-codes/${id}/deactivate`);
      toast.success(`${code} deactivated`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (user && !user.is_admin) return <Navigate to="/" replace />;
  if (!user) return <div className="p-20 text-center font-serif text-2xl">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-14" data-testid="admin-promo-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Admin</div>
          <h1 className="font-serif text-5xl mt-2 inline-flex items-center gap-3">
            <Ticket className="h-9 w-9" style={{ color: "var(--terracotta)" }} />
            Promo codes
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            Discounts for Maker Pro subscriptions. Codes are created in Stripe — always the source of truth.
          </p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="promo-new-btn" className="btn-primary rounded-full">
              <Plus className="h-4 w-4 mr-1.5" /> New code
            </Button>
          </DialogTrigger>
          <CreateDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
        </Dialog>
      </div>

      <div className="mt-10 card-warm rounded-sm overflow-hidden" data-testid="promo-list">
        {codes === null ? (
          <div className="p-10 text-center font-serif text-2xl" style={{ color: "var(--ink-soft)" }}>Loading…</div>
        ) : codes.length === 0 ? (
          <div className="p-14 text-center" data-testid="promo-empty">
            <div className="font-serif text-2xl">No codes yet.</div>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              Click <em>New code</em> to spin up your first launch discount.
            </p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--border-warm)" }}>
            {codes.map((p) => (
              <div key={p.id} className="p-5 flex items-center justify-between gap-4 flex-wrap"
                   data-testid={`promo-row-${p.code}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => copy(p.code)}
                      className="font-mono text-lg tracking-widest inline-flex items-center gap-2 hover:underline"
                      data-testid={`promo-code-${p.code}`}
                      title="Click to copy"
                    >
                      {p.code}
                      <Copy className="h-3 w-3" style={{ color: "var(--ink-soft)" }} />
                    </button>
                    <StatusBadge p={p} />
                  </div>
                  <div className="mt-1.5 text-sm" style={{ color: "var(--ink-soft)" }}>
                    <strong style={{ color: "var(--terracotta)" }}>
                      {p.amount_off != null
                        ? `$${p.amount_off.toFixed(2)} off`
                        : `${p.percent_off}% off`}
                    </strong>
                    {" · "}
                    {p.duration === "forever"
                      ? "every bill, forever"
                      : p.duration === "repeating"
                        ? `for ${p.duration_amount || p.duration_in_months} ${p.duration_unit || (p.duration_in_months > 1 ? "months" : "month")}`
                        : "first cycle"}
                    {p.max_redemptions ? ` · ${p.times_redeemed}/${p.max_redemptions} used` : ` · ${p.times_redeemed} used`}
                    {` · expires ${formatExpires(p.expires_at)}`}
                  </div>
                </div>
                {p.active && (
                  <Button
                    variant="outline" size="sm" className="rounded-full"
                    data-testid={`promo-deactivate-${p.code}`}
                    onClick={() => deactivate(p.id, p.code)}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1.5" /> Deactivate
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
